<script setup>
import PageTitle from '@weathernews/btos-vuecomponent/with-onebox-common-elements/src/components/PageTitle.vue'
import ORadarMap from '@weathernews/mi-web-design/organisms/ORadarMap.vue'
import ARadarButton from '@weathernews/mi-web-design/atoms/ARadarButton.vue'
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { VueSpinnerPuff } from 'vue3-spinners'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const props = defineProps({
  mapPosition: { type: Object, required: true },
  title: { type: String, default: '' }
})

const mapContainer = ref(null)
const mapCenter = ref(props.mapPosition.center)
const zoomLevel = ref(props.mapPosition.zoom)
const baseMapType = useCookie('baseMap', { default: () => 'osm-basic' })
const bgOpacity = useCookie('bgOpacity', { default: () => 0 })
const echoOpacity = useCookie('echoOpacity', { default: () => 0.8 })
const isRadarAnimation = useCookie('radarAnimation', { maxAge: 60 * 60 * 24 * 30 })
const formatTitle = props.title ? `${props.title}の黄砂レーダー` : '黄砂レーダー'
const isLoading = ref(false)
const isOverlayOpen = ref(false)

const handleCustomGeolocateLoading = (e) => {
  if (e && e.detail && typeof e.detail.loading === 'boolean') {
    isLoading.value = e.detail.loading
  }
}

if (import.meta.client) {
  window.addEventListener('custom-geolocate-loading', handleCustomGeolocateLoading)
  onUnmounted(() => {
    window.removeEventListener('custom-geolocate-loading', handleCustomGeolocateLoading)
  })
}

const { data: kosaData } = await useFetch('/api/kosa/kosaData')
const sliderValue = ref(0)

const { initMap, updateKosaTile, loadedMap, tmpMarkerPosition, isConfigOpen } = useKosaMap(zoomLevel, baseMapType, bgOpacity, echoOpacity, kosaData.value.maxZoom)

const onClickButton = () => {
  isOverlayOpen.value = true
}

watch(() => sliderValue.value, (newValue) => {
  updateKosaTile(kosaData.value.tile[newValue].d)
})

watch(() => bgOpacity.value, (newValue) => {
  if (loadedMap.value) {
    loadedMap.value.setPaintProperty('gray-overlay', 'background-opacity', newValue)
  }
})

watch(() => echoOpacity.value, (newValue) => {
  if (loadedMap.value) {
    loadedMap.value.setPaintProperty('kosa-tiles', 'raster-opacity', newValue)
  }
})

watch(() => isConfigOpen.value, (newVal) => {
  if (newVal) isOverlayOpen.value = false
})
watch(() => isOverlayOpen.value, (newVal) => {
  if (newVal) isConfigOpen.value = false
})

onMounted(async () => {
  const firstTileUrl = kosaData.value.tile[sliderValue.value].d
  await initMap(mapContainer.value, mapCenter, props.mapPosition.bounds, firstTileUrl)
  if (!isRadarAnimation.value) {
    isOverlayOpen.value = true
    isRadarAnimation.value = 1
    setTimeout(() => {
      isOverlayOpen.value = false
    }, 1500)
  }
  zoomLevel.value = loadedMap.value.getZoom()
  mapCenter.value = [loadedMap.value.getCenter().lng, loadedMap.value.getCenter().lat]
})
</script>

<template>
  <section class="zoomMap">
    <PageTitle
      :title="formatTitle"
      :text="`${dayjs.unix(kosaData.announced).tz('Asia/Tokyo').format('D日H:mm')}現在`"
    />
    <div
      class="map"
      data-main
    >
      <ORadarMap>
        <template #main>
          <img
            src="https://gvs.weathernews.jp/onebox/radar/img/legend/legend_zoomradar_kosa.webp"
            alt="legend"
          >
          <div
            ref="mapContainer"
            class="map-container"
          >
            <div
              v-if="isLoading"
              class="loading-indicator"
            >
              <VueSpinnerPuff
                size="70"
                color="#ffffff"
              />
            </div>
          </div>
        </template>
        <template #front>
          <KosaRadarLinks
            v-model:is-overlay-open="isOverlayOpen"
            :map-center="mapCenter"
            :map-zoom="zoomLevel"
            :tmp-marker-position="tmpMarkerPosition"
          />
          <KosaMapSettingPanel
            v-model:is-config-open="isConfigOpen"
            v-model:base-map-type="baseMapType"
            v-model:bg-opacity="bgOpacity"
            v-model:echo-opacity="echoOpacity"
          />
        </template>
        <template #rightBottom>
          <ARadarButton>
            <KosaRadarLinkButton
              v-if="!isOverlayOpen"
              category="kosa"
              label="黄砂"
              @click="onClickButton()"
            />
          </ARadarButton>
        </template>
      </ORadarMap>
      <KosaTimeZagSlider
        v-model:slider-value="sliderValue"
        :label-list="kosaData.tile.map(item => item.tm)"
      />
    </div>
  </section>
</template>

<style lang="scss" scoped>
.zoomMap {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .map {
    position: relative;
    img {
      position: absolute;
      top: 5px;
      left: 5px;
      width: 230px;
      height: auto;
      z-index: 10;
    }
    .map-container {
      position: relative;
      width: 100%;
      height: 100%;
      .loading-indicator {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(0, 0, 0, 0.75);
        z-index: 100;
        pointer-events: none;
      }
    }
  }
}

:deep(.maplibregl-ctrl-group) {
  position: relative;
  box-shadow: 0 2px 4px 0 rgb(0 0 0 / .25);
}
:deep(.maplibregl-ctrl-group:has(.circle)) {
  border-radius: 50%;
  border: 2px solid #FFF;
  background-color: #E6EAEC;
}
:deep(.maplibregl-ctrl-group button) {
  width: 30px;
  height: 30px;
}
:deep(.maplibregl-ctrl-group button.circle) {
  width: 35px;
  height: 35px;
}
:deep(.maplibregl-ctrl-group button:is(.map__balloon *)) {
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 4px 0 rgb(0 0 0 / .25);
}
:deep(.maplibregl-ctrl-bottom-left) {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;
  left: 0;
  bottom: 0;
  padding: 7px;
  pointer-events: auto;
}
:deep(.maplibregl-ctrl-bottom-left > .maplibregl-ctrl) {
  margin: 0;
}
:deep(.maplibregl-ctrl-bottom-left .maplibregl-compact) {
  margin-inline: 3px;
}
:deep(.maplibregl-ctrl-bottom-right > .maplibregl-ctrl) {
  margin: 0;
}
:deep(.maplibregl-ctrl-attrib a ) {
  font-size: 10px;
}
@media screen and (min-width: 830px) {
  .zoomMap {
    .map {
      &__radars {
        &-inner {
          width: auto;
          &:is(.active *) {
            width: auto;
          }
        }
        &::before {
          display: none;
        }
      }
      .map-container {
        aspect-ratio: 151 / 100;
        .map__buttons {
          padding: 9px;
        }
      }
    }
  }
  :deep(.maplibregl-ctrl-group:has(.circle)) {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 150px;
    padding: 5px 8px 5px 5px;
    border-radius: 6px;
    border: none;
    background-color: #FFF;
    cursor: pointer;
    transition: background-color .2s ease-in-out;
  }
  :deep(.maplibregl-ctrl-group:has(.circle):hover) {
    background-color: #F2F2F2;
  }
  :deep(.maplibregl-ctrl-group button) {
    width: 38px;
    height: 38px;
  }
  :deep(.maplibregl-ctrl-group button.circle) {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background-color: #E6EAEC;
  }
  :deep(.maplibregl-ctrl-bottom-left .maplibregl-compact) {
    margin-inline: 7px;
    margin-bottom: 10px;
  }
  :deep(.maplibregl-ctrl-bottom-left) {
    gap: 16px;
    padding: 9px;
  }
}
</style>
